<script setup lang="ts">
import "@schedule-x/theme-default/dist/index.css";

import { computed, getCurrentInstance, onMounted, onUnmounted, onUpdated, ref, shallowRef, watch } from "vue";
import { DateTime } from "luxon";
import { createCalendar, createViewDay, createViewMonthAgenda, createViewWeek } from "@schedule-x/calendar";
import { CalendarApp, viewMonthGrid } from "@schedule-x/calendar";
import { ScheduleXCalendar } from "@schedule-x/vue";
import { createCalendarControlsPlugin } from "@schedule-x/calendar-controls";
import { createScrollControllerPlugin } from "@schedule-x/scroll-controller";
import { esES, enGB, itIT, ptBR } from "@schedule-x/translations";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import { createCurrentTimePlugin } from "@schedule-x/current-time";

import type { IANATimezone } from "./Plugins/BasePlugin";
import type { CalendarControl } from "./Plugins/CalendarControl";
import type { EventService } from "./Plugins/EventServiceInterface";
import type { Calendar, FullCalendarEvent } from "@/Modules/Calendars/Models/Domain";
import type { CalendarEvent, CalendarType, PluginBase } from "@schedule-x/calendar";
import { Language } from "@/Common/Models/Domain/Language";

import useUserStore from "@/Modules/User/Store";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import {
  getLocale,
  transformFullCalendarEventToCalendarEvent,
  transformCalendarToCalendarType,
  closestNumber,
  zonedPlainDateTimeToDateTime,
  plainDateToDateTime,
} from "./utils";

import { CALENDAR_VIEW, SELECTED_DATE_FORMAT, WEEK_HOUR_HEIGHT } from "./constants";

import Header from "./components/Header.vue";

interface XCalendatProps {
  events: FullCalendarEvent[];
  calendars: Calendar[];
  exactTime?: boolean;
  startDate?: DateTime;
  currentTime?: boolean;
  userTimeZone?: string;
}

const props = withDefaults(defineProps<XCalendatProps>(), {
  exactTime: false,
  currentTime: true,
});

const emits = defineEmits<{
  (e: "eventClick", event: FullCalendarEvent): void;
  (e: "newEvent", value: DateTime | undefined): void; // UTC DATETIME
  (e: "eventServiceReady", event: EventService): void;
  (e: "calendarServiceReady", event: CalendarControl): void;
  (e: "nextDate", date: DateTime): void;
  (e: "prevDate", date: DateTime): void;
}>();

const userStore = useUserStore();

const lastCalendarsList = ref<Calendar[]>(props.calendars);
const calendar = shallowRef<CalendarApp | null>(null);

let currentTimeService = undefined;
const eventsService = createEventsServicePlugin();
const calendarControl = createCalendarControlsPlugin() as unknown as CalendarControl;
const scrollControl = createScrollControllerPlugin({
  initialScroll: "08:00",
});

const currentSelectedDate = ref<string>(
  (hasValue(props.startDate) ? props.startDate : DateTime.now()).toFormat(SELECTED_DATE_FORMAT)
);

const calendarsRecord = computed(() =>
  props.calendars.reduce<Record<string, CalendarType>>((acc, calendar) => {
    acc[calendar.uuid] = transformCalendarToCalendarType(calendar);

    return acc;
  }, {})
);

const events = computed((): CalendarEvent[] =>
  props.events.map((e) => transformFullCalendarEventToCalendarEvent(e))
);

const extraEventStyles = computed((): string => {
  const calendarStyles = props.calendars.map(
    (c: Calendar) => `
      .gd-calendar .event--calendar-${c.uuid}.event--past {
        background-color: color-mix(in srgb, ${c.backgroundColor}, white 70%) !important;
      }

      .gd-calendar .event--calendar-${c.uuid}.event--today {
        background-color: color-mix(in srgb, ${c.backgroundColor}, white 30%) !important;
      }
  `
  );

  return calendarStyles.join("");
});

const getEvent = (event: CalendarEvent): FullCalendarEvent => {
  return props.events.find((e: FullCalendarEvent) => e.uuid === event.id)!;
};

const handleChangeView = (view: CALENDAR_VIEW) => {
  calendarControl.setView(view as string);
};

const handleAddEvent = (date?: Temporal.ZonedDateTime) => {
  if (!hasValue(date)) {
    emits("newEvent", undefined);
  } else {
    let datetime = zonedPlainDateTimeToDateTime(date);

    if (!props.exactTime) {
      const startMinute = closestNumber(date.minute, [0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55])!;

      datetime = datetime.set({ minute: startMinute, second: 0 });
    }

    emits("newEvent", datetime);
  }
};

const handleNextPeriod = (date: DateTime) => emits("nextDate", date);
const handlePrevPeriod = (date: DateTime) => emits("prevDate", date);

const watchCalendars = watch(
  () => props.calendars.length,
  (post, prev) => {
    if (post < prev) {
      calendarControl.setCalendars(calendarsRecord.value);

      const deletedCalendarsMap = new Map<string, Calendar>();

      lastCalendarsList.value.forEach((c) => {
        if (calendarsRecord.value[c.uuid] === undefined) {
          deletedCalendarsMap.set(c.uuid, c);
        }
      });

      const events = eventsService.getAll();

      events.forEach((event) => {
        if (isValidString(event.calendarId) && deletedCalendarsMap.has(event.calendarId)) {
          const calendar = deletedCalendarsMap.get(event.calendarId)!;

          if (!isValidString(calendar.originCalendarUuid)) {
            eventsService.remove(event.id);
          } else {
            eventsService.update({
              ...event,
              calendarId: calendar.originCalendarUuid,
            });
          }
        }
      });
    }
  }
);

onMounted(() => {
  lastCalendarsList.value = props.calendars;

  const services: PluginBase<string>[] = [eventsService, calendarControl, scrollControl];

  if (props.currentTime) {
    currentTimeService = createCurrentTimePlugin();

    services.push(currentTimeService);
  }

  calendar.value = createCalendar(
    {
      locale: getLocale(userStore.user?.language || Language.SPANISH),
      selectedDate: Temporal.PlainDate.from(currentSelectedDate.value),
      defaultView: viewMonthGrid.name,
      timezone: isValidString(props.userTimeZone) ? (props.userTimeZone as IANATimezone) : undefined,
      weekOptions: {
        gridHeight: WEEK_HOUR_HEIGHT * 24,
        timeAxisFormatOptions: {
          hour: "2-digit",
          minute: "2-digit",
        },
      },
      isResponsive: false,
      skipValidation: true,
      views: [createViewWeek(), createViewMonthAgenda(), createViewDay()],
      events: events.value,
      calendars: calendarsRecord.value,
      callbacks: {
        onEventClick(event) {
          emits("eventClick", getEvent(event));
        },

        onRender() {
          emits("eventServiceReady", eventsService as unknown as EventService);
          emits("calendarServiceReady", calendarControl as unknown as CalendarControl);
        },

        onSelectedDateUpdate(date: Temporal.PlainDate) {
          currentSelectedDate.value = plainDateToDateTime(date).toFormat(SELECTED_DATE_FORMAT); // YYYY-MM-DD
        },

        onClickDateTime(dateTime: Temporal.ZonedDateTime) {
          handleAddEvent(dateTime);
        },
      },
      translations: {
        esES,
        enGB,
        itIT,
        ptBR,
      },
    },
    services
  );
});

onUpdated(() => {
  if (!hasValue(getCurrentInstance()?.vnode?.props?.onEventClick)) {
    eventsService.set(events.value);
  }
});

onUnmounted(() => {
  watchCalendars();
});
</script>

<template>
  <ScheduleXCalendar v-if="hasValue(calendar)" class="gd-calendar" :calendarApp="calendar">
    <template #headerContent="{ $app }">
      <Header
        :app="$app"
        :showDate="currentSelectedDate"
        :controls="calendarControl"
        @changeView="handleChangeView"
        @addEvent="handleAddEvent"
        @nextDate="handleNextPeriod"
        @prevDate="handlePrevPeriod"
      />

      <component v-bind:is="'style'"> {{ extraEventStyles }} </component>
    </template>
  </ScheduleXCalendar>
</template>

<style lang="scss">
.gd-calendar {
  .sx {
    &__calendar {
      border-radius: 0;
      border: 0;

      &-header {
        padding: 0;
      }
    }

    &__view-container {
      @include utils.noScrollbar();
    }

    &__week-grid__date {
      flex-flow: column-reverse;

      &--is-today {
        .sx__week-grid__date-number {
          @include colors.bg(primary, high);
          @include colors.color(content, inverse);
        }

        .sx__week-grid__day-name {
          @include colors.color(content, high);
        }
      }
    }

    &__month-agenda-day {
      height: $space-22;

      @media (max-width: $breakpoint-m) {
        height: $space-18;
      }
    }

    &__time-grid-event {
      @include sp.padding(y, 0);

      padding-left: var(--sx-spacing-padding1);
      padding-right: var(--sx-spacing-padding1);
      display: flex;
      flex-direction: column;
      align-items: start;
    }
  }
}
</style>
